import classNames from 'classnames';
import React from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { getConstructionTypeText } from '../../utils/data/elementConstructionTypeList';
import { usePatchedDropdownMenu } from '../../utils/usePatchedDropdownMenu';
import { IElement } from '../interfaces/IElement';
import { IManifestation } from '../interfaces/IManifestation';
import { IUnit } from '../interfaces/IUnit';
import { NullableBoolean } from '../utils/NullableBoolean';
import { UnitOfMeasurement } from '../utils/UnitOfMeasurement';

type UnitProps = {
    unit: IUnit;
    showAddManifestations: boolean;
    submitting: boolean;
    elementId: string;
    onAddUnit: (id: string, code: string) => void;
    onEditUnit: (elementId: string, unitId: string) => void;
    onRemoveUnit: (unit: IUnit) => void;
    onAddManifestations: (unit: IUnit) => void;
};

function UnitActionsRowCell(props: UnitProps) {
    const translate = useTranslate();
    const isDeletable =
        props.unit.manifestations.length === 0 &&
        props.unit.assetsCount === 0 &&
        props.unit.conditionsCount === 0 &&
        props.unit.failureModeCount === 0;

    const menuItemsAmount = (() => {
        let amount = 0;

        if (isDeletable) {
            amount++;
        }

        if (props.showAddManifestations) {
            amount++;
        }

        return ++amount;
    })();

    const {
        buttonProps: bodyButtonProps,
        itemProps: bodyItemProps,
        isOpen: isBodyMenuOpen,
        setIsOpen: setIsBodyMenuOpen,
    } = usePatchedDropdownMenu(menuItemsAmount);

    const headerClasses = classNames('btn-group', { open: isBodyMenuOpen });

    const editUnitClickHandler = () => {
        setIsBodyMenuOpen(false);
        props.onEditUnit(props.elementId, props.unit.id);
    };

    const removeUnitClickHandler = () => {
        if (window.confirm(translate('common.confirmDeleteItem'))) {
            props.onRemoveUnit(props.unit);
        }
    };

    const addManifestationsClickHandler = () => {
        setIsBodyMenuOpen(false);
        props.onAddManifestations(props.unit);
    };

    const getDeletaMenuItem = () =>
        isDeletable ? (
            <li onClick={removeUnitClickHandler} {...bodyItemProps[1]}>
                <a className="cursor">{translate('common.remove')}</a>
            </li>
        ) : null;

    const getManifestationsMenuItem = () => {
        if (!props.showAddManifestations) {
            return null;
        }

        return (
            <>
                <li className="divider"></li>
                <li {...bodyItemProps[Number(isDeletable) + 1]} onClick={addManifestationsClickHandler}>
                    <a className="cursor edit-unit">{translate('common.addItem', translate('common.manifestations'))}</a>
                </li>
            </>
        );
    };

    return (
        <div className={headerClasses}>
            <button
                {...bodyButtonProps}
                className="btn btn-primary btn-sm fs12 fw500 dropdown-toggle ph10"
                data-toggle="dropdown"
                disabled={props.submitting}
                type="button"
            >
                {translate('common.actions')}
                <span className="caret"></span>
            </button>

            <ul className="dropdown-menu pull-right" role="menu">
                <li {...bodyItemProps[0]} onClick={editUnitClickHandler}>
                    <a className="cursor edit-element">{translate('common.edit')}</a>
                </li>
                {getDeletaMenuItem()}
                {getManifestationsMenuItem()}
            </ul>
        </div>
    );
}

interface IElementRowProps {
    element: IElement;
    submitting: boolean;
    showAddManifestations: boolean;
    onRemoveElement: (element: IElement) => void;
    onRemoveUnit: (unit: IUnit) => void;
    onEditElement: (id: string) => void;
    onAddUnit: (id: string, code: string) => void;
    onEditUnit: (elementId: string, unitId: string) => void;
    onAddManifestations: (unit: IUnit) => void;
    onRemoveManifestation: (manifestation: IManifestation) => void;
    onEditManifestation: (manifestation: IManifestation) => void;
}

export function ElementRow(props: IElementRowProps) {
    const translate = useTranslate();
    const isDeleteInHeaderPossible = props.element.units.length === 0 && props.element.assetsCount === 0 && props.element.conditionsCount === 0;

    const headerMenuItemsAmount = ((): number => {
        let amount = 1;

        if (isDeleteInHeaderPossible) {
            amount++;
        }

        return ++amount;
    })();

    const {
        buttonProps: headerButtonProps,
        itemProps: headerItemProps,
        isOpen: isHeaderMenuOpen,
        setIsOpen: setIsHeaderMenuOpen,
    } = usePatchedDropdownMenu(headerMenuItemsAmount);

    const headerClasses = classNames('btn-group', { open: isHeaderMenuOpen });

    const onDeleteElementHandler = (): void => {
        const message = translate('common.confirmDeleteItem');
        const isDeletionConfirmed = window.confirm(message);

        if (isDeletionConfirmed) {
            props.onRemoveElement(props.element);
        }

        setIsHeaderMenuOpen(false);
    };

    const onEditHandler = (): void => {
        setIsHeaderMenuOpen(false);
        props.onEditElement(props.element.id);
    };

    const onAddUnitHandler = (): void => {
        props.onAddUnit(props.element.id, props.element.code);
    };

    const getMenuItemsForDeletableCase = () => {
        if (!isDeleteInHeaderPossible) {
            return null;
        }

        return (
            <li {...headerItemProps[1]} onClick={onDeleteElementHandler}>
                <a className="cursor">{translate('common.remove')}</a>
            </li>
        );
    };

    const getMenuItemsForEditableCase = () => (
        <>
            <li {...headerItemProps[0]} onClick={onEditHandler}>
                <a className="cursor edit-element">{translate('common.edit')}</a>
            </li>
            {getMenuItemsForDeletableCase()}
            <li className="divider"></li>
        </>
    );

    return (
        <>
            <thead key={props.element.id}>
                <tr className="primary">
                    <th>{props.element.code}</th>
                    <th>
                        {props.element.name}
                        {props.element.isArchived ? <i className="fa fa-ban ml10"></i> : ''}
                    </th>
                    <th>
                        <NullableBoolean value={props.element.surveyId !== null} useSymbols />
                    </th>
                    <th>{props.element.constructionType && getConstructionTypeText(props.element.constructionType)}</th>
                    <th>&nbsp;</th>
                    <th>{props.element.constructionYear}</th>
                    <th className="text-right">
                        <div className={headerClasses}>
                            <button
                                {...headerButtonProps}
                                className="btn btn-primary btn-sm fs12 fw500 dropdown-toggle ph10"
                                data-toggle="dropdown"
                                disabled={props.submitting}
                                type="button"
                            >
                                {translate('common.actions')}
                                <span className="caret"></span>
                            </button>

                            <ul className="dropdown-menu pull-right" role="menu">
                                {getMenuItemsForEditableCase()}
                                <li>
                                    <a {...headerItemProps[headerMenuItemsAmount - 1]} className="cursor edit-unit" onClick={onAddUnitHandler}>
                                        {translate('common.addItem', translate('common.unit'))}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody key={`${props.element.id}tbody`}>
                {props.element.units.map((unit) => (
                    <React.Fragment key={unit.id}>
                        <tr className="bg-light darker">
                            <td>{unit.code}</td>
                            <td>
                                {unit.name}
                                {unit.isArchived ? <i className="fa fa-ban ml10"></i> : ''}
                            </td>
                            <td>
                                {' '}
                                <NullableBoolean value={unit.surveyId !== null} useSymbols />
                            </td>
                            <td>{unit.material}</td>
                            <td>
                                {unit.quantity}
                                <UnitOfMeasurement value={unit.quantityUnitOfMeasurement} />
                            </td>
                            <td>{unit.constructionYear}</td>
                            <td className="text-right">
                                <UnitActionsRowCell
                                    unit={unit}
                                    showAddManifestations={props.showAddManifestations}
                                    submitting={props.submitting}
                                    elementId={props.element.id}
                                    onAddUnit={props.onAddUnit}
                                    onEditUnit={props.onEditUnit}
                                    onRemoveUnit={props.onRemoveUnit}
                                    onAddManifestations={props.onAddManifestations}
                                />
                            </td>
                        </tr>

                        {unit.manifestations.map((manifestation) => (
                            <tr className="secundary" key={manifestation.id}>
                                <td>{manifestation.code}</td>
                                <td>{manifestation.name}</td>
                                <td>
                                    <NullableBoolean value={manifestation.surveyId !== null} useSymbols />
                                </td>
                                <td>&nbsp;</td>
                                <td>&nbsp;</td>
                                <td>&nbsp;</td>
                                <td className="text-right">
                                    <button
                                        type="button"
                                        disabled={
                                            manifestation.assetsCount > 0 || manifestation.conditionsCount > 0 || manifestation.failureModeCount > 0
                                        }
                                        onClick={() => {
                                            if (window.confirm(translate('common.confirmDeleteItem'))) {
                                                props.onRemoveManifestation(manifestation);
                                            }
                                        }}
                                        className="btn btn-default btn-sm fs12"
                                        title={translate('common.remove')}
                                    >
                                        <span className="fa fa-trash-o"></span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => props.onEditManifestation(manifestation)}
                                        className="btn btn-default btn-sm fs12 edit-manifestation"
                                        title={translate('common.edit')}
                                    >
                                        <span className="fa fa-edit"></span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </React.Fragment>
                ))}
            </tbody>
        </>
    );
}
